import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { FetchAssetParams, CarrierSetting } from '../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { StoreModel } from '../..';

export interface CarrierSettingsModel {
  fetchCarrierSettings: Thunk<CarrierSettingsModel, FetchAssetParams, Injections, Promise<CarrierSetting[]>>;
  carrierSettingsActionStart: Action<CarrierSettingsModel, void>;
  lastAction?: number;
  carrierSettings: Record<string, CarrierSetting>;
  setCarrierSettings: Action<CarrierSettingsModel, CarrierSetting[]>;
  carrierSettingsFailure: Action<CarrierSettingsModel, ApplicationErrorOptions>;
  createCarrierSettings: Thunk<
    CarrierSettingsModel,
    Partial<CarrierSetting>,
    Injections,
    StoreModel,
    Promise<CarrierSetting>
  >;
  updateCarrierSettings: Thunk<
    CarrierSettingsModel,
    Partial<CarrierSetting>,
    Injections,
    StoreModel,
    Promise<CarrierSetting>
  >;
}

export const carrierSettingsModel: CarrierSettingsModel = {
  carrierSettings: {},
  carrierSettingsFailure: action((state, payload) => {
    console.error(payload);
  }),
  setCarrierSettings: action((state, payload) => {
    for (const val of payload) {
      state.carrierSettings[val.carrierSettingsId] = val;
    }
  }),
  carrierSettingsActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  fetchCarrierSettings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.carrierSettingsActionStart();
      const res = await injections.assetService.fetchCarrierSettings(payload);
      actions.setCarrierSettings(res);
      return res;
    } catch (error) {
      actions.carrierSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching carrier rates.',
        asNotification: true,
      });
    }
  }),
  createCarrierSettings: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.carrierSettingsActionStart();
      const res = await injections.assetService.createCarrierSettings(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-settings-create',
        entityId: payload.carrierSettingsId,
      });

      actions.setCarrierSettings([res]);
      return res;
    } catch (error) {
      actions.carrierSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching carrier rates.',
        asNotification: true,
      });
      return error;
    }
  }),
  updateCarrierSettings: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.carrierSettingsActionStart();
      const res = await injections.assetService.updateCarrierSettings(payload);

      // Invalidate fulfillment contexts queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-settings-update',
        entityId: payload.carrierSettingsId,
      });

      actions.setCarrierSettings([res]);
      return res;
    } catch (error) {
      actions.carrierSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching carrier rates.',
        asNotification: true,
      });
      return error;
    }
  }),
};
